/**
 * Multi-page continuous virtualized scroll.
 *
 * Pages render lazily via IntersectionObserver (only pages within ~1 viewport
 * of the visible area get full canvas + overlay rendering). Off-viewport pages
 * show stub placeholders.
 *
 * Public API:
 *   buildPageFrames(scrollContainer, pageSizes)             — DOM scaffold.
 *   classifyPageZones(visibleRange, totalPages, opts)       — pure: zone map.
 *   nearestPageInViewport(pageRects, scrollY, viewportH)    — pure: 1-based.
 *   visiblePageRange(pageRects, scrollY, viewportH)         — pure: range.
 *   MultiPageScrollManager(hooks)                           — orchestrator.
 *
 * @module multi-page-scroll
 */
import { PDF_US_LETTER } from "./dom-utils.js";
// ─── Performance instrumentation seam (Phase FB.12) ─────────────────────────
//
// `performance.mark` / `performance.measure` pair around the
// MultiPageScrollManager `refresh()` callback. The FB.12 perf fixture
// (web/static/editor/test/perf-500-page.test.mjs) reads measure durations
// via `performance.getEntriesByType('measure')` to assert against the
// §P.1 frame budget (16 ms per refresh on 500-page docs). Zero-overhead
// when `performance` is unavailable.
const _perf = (() => {
    if (typeof performance !== "undefined"
        && typeof performance.mark === "function"
        && typeof performance.measure === "function") {
        return performance;
    }
    return null;
})();
let _perfSeq = 0;
/**
 * Three zones, in order from cheapest to most expensive:
 *   stub      — height-only placeholder; no canvas, no overlay.
 *   canvas    — canvas painted; overlay div empty.
 *   overlays  — canvas painted AND overlay populated from WASM bbox data.
 */
export const PageZone = Object.freeze({
    STUB: "stub",
    CANVAS: "canvas",
    OVERLAYS: "overlays",
});
const DEFAULT_BUFFER_PAGES = 1; // Pages above and below viewport that get full overlays.
const DEFAULT_CANVAS_BUFFER = 3; // Pages above and below viewport that get canvas-only.
// ─── Pure helpers ────────────────────────────────────────────────────────────
/**
 * Build the per-page zone classification given the currently-visible page
 * range and the total page count. Pages within ±buffer of the visible
 * range get OVERLAYS; pages within ±canvasBuffer get CANVAS; rest stay STUB.
 */
export function classifyPageZones(visibleRange, totalPages, opts) {
    const overlayBuffer = (opts && typeof opts.overlayBuffer === "number") ? opts.overlayBuffer : DEFAULT_BUFFER_PAGES;
    const canvasBuffer = (opts && typeof opts.canvasBuffer === "number") ? opts.canvasBuffer : DEFAULT_CANVAS_BUFFER;
    const out = new Map();
    if (!visibleRange || !Number.isFinite(totalPages) || totalPages <= 0)
        return out;
    const first = Math.max(1, visibleRange.first);
    const last = Math.min(totalPages, visibleRange.last);
    for (let p = 1; p <= totalPages; p++) {
        const dToFirst = first - p;
        const dToLast = p - last;
        const dist = Math.max(dToFirst, dToLast, 0); // 0 inside visible range
        if (dist <= overlayBuffer)
            out.set(p, PageZone.OVERLAYS);
        else if (dist <= canvasBuffer)
            out.set(p, PageZone.CANVAS);
        else
            out.set(p, PageZone.STUB);
    }
    return out;
}
/**
 * Compute the page nearest the viewport center given the page-rect map
 * (1-based page → { top, height }) and scroll position.
 *
 * @returns 1-based page number, or 1 if pageRects is empty.
 */
export function nearestPageInViewport(pageRects, scrollY, viewportH) {
    if (!pageRects || pageRects.size === 0)
        return 1;
    const center = scrollY + viewportH / 2;
    let bestPage = 1;
    let bestDist = Infinity;
    for (const [page, rect] of pageRects) {
        const pageCenter = rect.top + rect.height / 2;
        const d = Math.abs(pageCenter - center);
        if (d < bestDist) {
            bestDist = d;
            bestPage = page;
        }
    }
    return bestPage;
}
/**
 * Compute the visible-page range from the per-page rect map and scroll.
 */
export function visiblePageRange(pageRects, scrollY, viewportH) {
    if (!pageRects || pageRects.size === 0)
        return { first: 1, last: 1 };
    const top = scrollY;
    const bottom = scrollY + viewportH;
    let first = null;
    let last = null;
    for (const [page, rect] of pageRects) {
        const pTop = rect.top;
        const pBot = rect.top + rect.height;
        if (pBot >= top && pTop <= bottom) {
            if (first == null || page < first)
                first = page;
            if (last == null || page > last)
                last = page;
        }
    }
    if (first == null)
        first = 1;
    if (last == null)
        last = first;
    return { first, last };
}
/**
 * Build the per-page frames inside the scroll container. Each frame is
 * given a fixed pixel height matching the page's natural height at the
 * current zoom. Initially all frames are stubs.
 */
export function buildPageFrames(scrollContainer, pageSizes) {
    if (!scrollContainer)
        throw new Error("buildPageFrames: scrollContainer required");
    const doc = scrollContainer.ownerDocument || (typeof document !== "undefined" ? document : null);
    if (!doc)
        throw new Error("buildPageFrames: no document");
    scrollContainer.innerHTML = "";
    const frames = [];
    const pageRects = new Map();
    let cumTop = 0;
    // Bug 6 (INV-LBL-1): the "Page N" label must sit ADJACENT to its own frame,
    // not float in the inter-page gap hugging the NEXT page. Keep a small gap
    // between a frame and its own label, and place the full §4.3 inter-page gap
    // AFTER the label. (Was: 24px frame marginBottom BETWEEN the page and its
    // label, ~0 between the label and the next frame → label hugged page N+1.)
    const FRAME_LABEL_GAP = 4; // frame → its own label (small — adjacency)
    const LABEL_NEXT_GAP = 24; // §4.3 inter-page gap, now placed AFTER the label
    const LABEL_HEIGHT = 16; // label height allowance for the cumTop scroll math
    for (let i = 0; i < pageSizes.length; i++) {
        const sz = pageSizes[i] ?? PDF_US_LETTER;
        const page = i + 1;
        const frame = doc.createElement("div");
        frame.className = "pdf-page-frame";
        frame.dataset.page = String(page);
        frame.dataset.zone = PageZone.STUB;
        frame.style.position = "relative";
        frame.style.width = `${sz.width}px`;
        frame.style.height = `${sz.height}px`;
        frame.style.marginBottom = `${FRAME_LABEL_GAP}px`;
        // Stub placeholder body:
        const stub = doc.createElement("div");
        stub.className = "pdf-page-stub";
        stub.textContent = `Page ${page} — overlays loading…`;
        frame.appendChild(stub);
        // Page label (centered below, adjacent to its own frame). The inter-page
        // gap lives on the LABEL's marginBottom so the label hugs its own page.
        const label = doc.createElement("div");
        label.className = "pdf-page-label";
        label.id = `page-${page}`;
        label.textContent = `Page ${page}`;
        label.style.marginBottom = `${LABEL_NEXT_GAP}px`;
        scrollContainer.appendChild(frame);
        scrollContainer.appendChild(label);
        frames.push(frame);
        pageRects.set(page, { top: cumTop, height: sz.height });
        cumTop += sz.height + FRAME_LABEL_GAP + LABEL_HEIGHT + LABEL_NEXT_GAP;
    }
    return { frames, pageRects };
}
export function MultiPageScrollManager(hooks) {
    if (!hooks || !hooks.scrollContainer || !Array.isArray(hooks.frames) || !hooks.pageRects) {
        throw new Error("MultiPageScrollManager: container/frames/pageRects required");
    }
    const total = hooks.frames.length;
    let scheduled = false;
    let currentPage = 1;
    const lastZone = new Map();
    // Per-page pending render promises. Keyed by 1-based page number. Set when
    // renderCanvas() is invoked for a page; cleared when its Promise resolves.
    // scrollToPage(p) awaits this Promise so callers see scroll-then-paint
    // as a single operation rather than fire-and-forget.
    const pendingRender = new Map();
    // Bug 4: lifecycle + reflow state. `destroyed` makes a late rAF / resize
    // callback a no-op after teardown (second-order dynamics, design §H);
    // `resizeObserver` is torn down in destroy() so a re-mount (new file open)
    // cannot leak observers or storm refresh() on every resize tick.
    let destroyed = false;
    let resizeObserver = null;
    function _trackRender(p, frame) {
        if (typeof hooks.renderCanvas !== "function")
            return null;
        const result = hooks.renderCanvas(p, frame);
        if (!result || typeof result.then !== "function") {
            // Synchronous (or non-promise) renderCanvas: nothing to track.
            return null;
        }
        const resultPromise = result;
        // is-loading marker: the canvas paint is in flight. CSS uses this
        // attribute to render a centered spinner over a shaded backdrop
        // (see editor.css `.pdf-page-frame[data-loading="true"]`). Cleared
        // on settle.
        frame.setAttribute("data-loading", "true");
        // eslint-disable-next-line promise/prefer-await-to-then -- .finally() used for cleanup in sync context; _trackRender returns a Promise, cannot await inside
        const tracked = resultPromise.finally(() => {
            // Only clear if this is still the in-flight promise — a
            // subsequent re-promotion may have replaced it.
            if (pendingRender.get(p) === tracked) {
                pendingRender.delete(p);
                frame.removeAttribute("data-loading");
            }
        });
        pendingRender.set(p, tracked);
        return tracked;
    }
    function refresh() {
        // Bug 4: a rAF / ResizeObserver callback can land after destroy() (new
        // file open tears the manager down mid-frame). Re-classifying then would
        // re-invoke the hooks against torn-down state — no-op instead (§H).
        if (destroyed)
            return;
        // Phase FB.12 perf seam — mark/measure the full refresh body so the
        // perf fixture can assert per-frame budget (<16 ms). Best-effort —
        // perf observability never throws into the scroll loop.
        let _refreshId = null;
        if (_perf !== null) {
            _refreshId = `multi-page-scroll:refresh-${++_perfSeq}`;
            try {
                _perf.mark(`${_refreshId}-start`);
            }
            catch (_) { /* swallow */ }
        }
        const sc = hooks.scrollContainer;
        const scrollY = sc.scrollTop || 0;
        // Test stubs return CSS height; production uses clientHeight.
        const viewportH = sc.clientHeight || sc.offsetHeight || 800;
        const visible = visiblePageRange(hooks.pageRects, scrollY, viewportH);
        const zones = classifyPageZones(visible, total);
        for (let p = 1; p <= total; p++) {
            const z = zones.get(p) ?? PageZone.STUB;
            const prev = lastZone.get(p) ?? PageZone.STUB;
            const frame = hooks.frames[p - 1];
            if (!frame)
                continue;
            if (z === prev)
                continue;
            frame.dataset.zone = z;
            if (z === PageZone.CANVAS && prev === PageZone.STUB) {
                void _trackRender(p, frame);
            }
            else if (z === PageZone.OVERLAYS) {
                if (prev === PageZone.STUB) {
                    const canvasDone = _trackRender(p, frame);
                    if (typeof hooks.renderOverlays === "function") {
                        if (canvasDone) {
                            // WHY: renderOverlaysForPage reads canvasEl.dataset.pageNaturalHeight,
                            // which renderPageIntoFrame sets asynchronously. Calling renderOverlays
                            // before the canvas render completes leaves naturalHeight as "" → 0 →
                            // all bboxes are zero-area → nothing renders. Chain on the canvas promise.
                            const ro = hooks.renderOverlays;
                            // eslint-disable-next-line promise/prefer-await-to-then -- chained on canvas promise in sync refresh loop; await not available here
                            void canvasDone.then(() => ro(p, frame)).catch(() => { });
                        }
                        else {
                            // Canvas render was synchronous — naturalHeight is already set.
                            void hooks.renderOverlays(p, frame);
                        }
                    }
                }
                else if (typeof hooks.renderOverlays === "function") {
                    // WHY (bug2, 260910): prev === CANVAS. The page promoted
                    // STUB→CANVAS via _trackRender at :313-314; that canvas paint
                    // may STILL be in flight when the page now promotes
                    // CANVAS→OVERLAYS. Firing renderOverlays immediately reads
                    // canvasEl.dataset.pageNaturalWidth="" → naturalWidth=0 →
                    // overlays.ts scale-guard → zero-area bboxes → nothing renders
                    // (the "overlays only appear after a zoom re-render" symptom).
                    // Chain on the in-flight canvas promise, mirroring the
                    // STUB→OVERLAYS branch above; fire immediately only when the
                    // canvas render already settled (no pending promise).
                    const ro = hooks.renderOverlays;
                    const canvasPending = pendingRender.get(p);
                    if (canvasPending) {
                        // eslint-disable-next-line promise/prefer-await-to-then -- chained on canvas promise in sync refresh loop; await not available here
                        void canvasPending.then(() => ro(p, frame)).catch(() => { });
                    }
                    else {
                        void ro(p, frame);
                    }
                }
            }
            else if (z === PageZone.STUB && typeof hooks.demote === "function") {
                hooks.demote(p, frame);
            }
            lastZone.set(p, z);
        }
        const np = nearestPageInViewport(hooks.pageRects, scrollY, viewportH);
        if (np !== currentPage) {
            currentPage = np;
            if (typeof hooks.onPageChange === "function")
                hooks.onPageChange(np);
        }
        // Phase FB.12 perf seam — close the refresh measure. Wrapped in
        // try so a malformed mark-id never leaks into the scroll loop.
        if (_perf !== null && _refreshId !== null) {
            try {
                _perf.mark(`${_refreshId}-end`);
                _perf.measure("multi-page-scroll:refresh", `${_refreshId}-start`, `${_refreshId}-end`);
            }
            catch (_) { /* swallow */ }
        }
    }
    // WHY rAF over rIC: scroll-driven zone reclassification needs to land
    // within a frame so the lazy-load promotion stays paired with the
    // user's scroll motion. requestIdleCallback can defer many seconds
    // under CPU contention (Tier-2 card mounting, image decodes), causing
    // the visible "scroll past page 2 → canvas stays blank" symptom that
    // motivated the 260507 lazy-load RCA. requestAnimationFrame fires once
    // per ~16 ms paint cycle; the `scheduled` flag coalesces multiple
    // triggers (scroll, the bug-4 forced mount refresh, ResizeObserver resize
    // ticks) fired between paints into a single refresh.
    function scheduleRefresh() {
        if (destroyed || scheduled)
            return;
        scheduled = true;
        const raf = (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function")
            ? window.requestAnimationFrame.bind(window)
            : (fn) => setTimeout(fn, 16);
        raf(() => { scheduled = false; refresh(); });
    }
    function onScroll() {
        scheduleRefresh();
    }
    /**
     * Scroll to page p AND wait for its canvas paint to land. Returns a
     * Promise that resolves when:
     *   - p was already painted (no pending render): immediately;
     *   - p was newly promoted to CANVAS/OVERLAYS by this call: when its
     *     renderCanvas Promise resolves.
     *
     * Callers that want post-render behavior (focus a bbox on p, scroll a
     * card into view, run an a11y assertion) MUST await this Promise.
     */
    function scrollToPage(p) {
        const rect = hooks.pageRects.get(p);
        if (!rect)
            return Promise.resolve();
        try {
            hooks.scrollContainer.scrollTop = rect.top;
        }
        catch (_) { /* readonly in some test stubs; ignore */ }
        refresh();
        const pending = pendingRender.get(p);
        return pending || Promise.resolve();
    }
    hooks.scrollContainer.addEventListener("scroll", onScroll);
    // Initial classification against mount-time geometry.
    refresh();
    // Bug 4 (INV-DRAW-1): the mount-time refresh above runs BEFORE the editor's
    // hideLoading() settles the final layout (mountMultiPageScroll runs ahead of
    // editor-load-flow's hideLoading()). If that first classification saw stale /
    // zero geometry, a visible page could stay STUB with an empty overlay until
    // the user scrolls — "not drawn until I interact." Force one more refresh on
    // the next frame (layout-settled), and install a ResizeObserver so any later
    // reflow (hideLoading, pane resize, zoom) re-classifies deterministically —
    // reusing the editor-empty-state.ts ResizeObserver pattern (A.19). Both route
    // through the rAF-coalesced scheduleRefresh; torn down in destroy().
    scheduleRefresh();
    if (typeof ResizeObserver === "function") {
        resizeObserver = new ResizeObserver(() => { scheduleRefresh(); });
        resizeObserver.observe(hooks.scrollContainer);
    }
    return {
        refresh,
        scrollToPage,
        destroy() {
            destroyed = true;
            hooks.scrollContainer.removeEventListener("scroll", onScroll);
            if (resizeObserver) {
                resizeObserver.disconnect();
                resizeObserver = null;
            }
            lastZone.clear();
            pendingRender.clear();
        },
        currentPage: () => currentPage,
        pendingRenderFor: (p) => pendingRender.get(p) || null,
    };
}
